import React from "react";
import { Box, Typography } from "@mui/material";

export const BEER = {
  bg: "#050a14",
  panel: "#0d2137",
  panelSoft: "rgba(13,33,55,0.72)",
  border: "#1a3a5c",
  blue: "#29b6f6",
  glow: "#4fc3f7",
  gold: "#ffd700",
  amber: "#ff9800",
  green: "#00e676",
};

/** Fixed bubble field so positions stay stable across re-renders. */
const BUBBLES = Array.from({ length: 20 }, (_, i) => {
  const r = ((i * 9301 + 49297) % 233280) / 233280;
  const r2 = ((i * 4801 + 12907) % 233280) / 233280;
  return {
    left: `${(i * 5.1 + r * 4.5) % 100}%`,
    size: 5 + Math.round(r2 * 17),
    duration: `${9 + r * 10}s`,
    delay: `${-(r2 * 14).toFixed(2)}s`,
    opacity: 0.1 + r2 * 0.2,
  };
});

const stageKeyframes = {
  "@keyframes beerFadeUp": {
    from: { opacity: 0, transform: "translateY(22px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  "@keyframes beerFadeIn": {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
  "@keyframes beerBubbleRise": {
    "0%": { transform: "translateY(20px) scale(0.55)", opacity: 0 },
    "10%": { opacity: 1 },
    "85%": { opacity: 1 },
    "100%": { transform: "translateY(-102vh) scale(1.2)", opacity: 0 },
  },
  "@keyframes beerFloat": {
    "0%, 100%": { transform: "translateY(0) rotate(-2.5deg)" },
    "50%": { transform: "translateY(-14px) rotate(2.5deg)" },
  },
  "@keyframes beerShine": {
    "0%": { backgroundPosition: "220% center" },
    "100%": { backgroundPosition: "-120% center" },
  },
  "@keyframes beerSpotlight": {
    "0%, 100%": { opacity: 0.75, transform: "translate(-50%, -50%) scale(1)" },
    "50%": { opacity: 1, transform: "translate(-50%, -50%) scale(1.06)" },
  },
  "@keyframes beerBlink": {
    "0%, 100%": { opacity: 1, boxShadow: "0 0 0 0 rgba(255,215,0,0.55)" },
    "70%": { opacity: 0.5, boxShadow: "0 0 0 10px rgba(255,215,0,0)" },
  },
  "@keyframes beerDotFade": {
    "0%, 80%, 100%": { opacity: 0.15 },
    "40%": { opacity: 1 },
  },
  "@keyframes beerFill": {
    from: { height: "6%" },
    to: { height: "88%" },
  },
  "@keyframes beerFoamBob": {
    "0%, 100%": { transform: "translateY(0)" },
    "50%": { transform: "translateY(-3px)" },
  },
  "@keyframes beerSweep": {
    "0%": { transform: "translateX(-120%) skewX(-18deg)" },
    "60%, 100%": { transform: "translateX(320%) skewX(-18deg)" },
  },
};

/**
 * Full-screen KBC-style stage: dark blue arena, breathing spotlight, rising
 * beer bubbles and an amber "beer level" glow along the bottom edge.
 */
export const LobbyStage = ({ children }) => (
  <Box
    sx={{
      minHeight: "100vh",
      bgcolor: BEER.bg,
      position: "relative",
      overflowX: "hidden",
      overflowY: "auto",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      px: { xs: 2, sm: 3 },
      py: { xs: 4, sm: 5 },
      ...stageKeyframes,
    }}
  >
    <Box
      sx={{
        position: "fixed",
        top: "42%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: { xs: "160%", sm: "95%" },
        height: "85%",
        background:
          "radial-gradient(ellipse at center, rgba(41,182,246,0.18) 0%, rgba(41,182,246,0.05) 42%, transparent 68%)",
        pointerEvents: "none",
        animation: "beerSpotlight 6s ease-in-out infinite",
      }}
    />

    <Box sx={{ position: "fixed", inset: 0, pointerEvents: "none", overflow: "hidden" }}>
      {BUBBLES.map((b, i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            bottom: -30,
            left: b.left,
            width: b.size,
            height: b.size,
            borderRadius: "50%",
            border: "1px solid rgba(255,215,0,0.45)",
            background:
              "radial-gradient(circle at 32% 28%, rgba(255,255,255,0.55), rgba(255,215,0,0.16) 55%, transparent 70%)",
            opacity: b.opacity,
            animation: `beerBubbleRise ${b.duration} linear ${b.delay} infinite`,
          }}
        />
      ))}
    </Box>

    <Box
      sx={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        height: { xs: 90, sm: 130 },
        background:
          "linear-gradient(to top, rgba(255,152,0,0.22) 0%, rgba(255,215,0,0.07) 45%, transparent 100%)",
        pointerEvents: "none",
      }}
    />

    <Box
      sx={{
        position: "relative",
        zIndex: 1,
        width: "100%",
        maxWidth: 900,
        m: "auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
      }}
    >
      {children}
    </Box>
  </Box>
);

/** Gold gradient wordmark with a slow light sweep across the letters. */
export const BrandLockup = ({ kicker = "WHO WANTS TO", size = "lg", delay = 0 }) => (
  <>
    <Typography
      sx={{
        color: BEER.blue,
        fontWeight: 800,
        letterSpacing: { xs: 2.5, sm: 6 },
        fontSize: { xs: "0.62rem", sm: "0.8rem" },
        mb: { xs: 0.6, sm: 0.9 },
        animation: `beerFadeUp 0.7s ease ${delay}s both`,
      }}
    >
      {kicker}
    </Typography>
    <Typography
      component="h1"
      sx={{
        fontWeight: 900,
        lineHeight: 1,
        letterSpacing: { xs: -0.5, sm: 0.5 },
        fontSize:
          size === "sm"
            ? { xs: "1.65rem", sm: "2.3rem", md: "2.7rem" }
            : { xs: "2.05rem", sm: "3.3rem", md: "4.1rem" },
        background:
          "linear-gradient(100deg, #b8860b 0%, #ffd700 28%, #fffbe6 44%, #ffd700 60%, #b8860b 100%)",
        backgroundSize: "220% auto",
        WebkitBackgroundClip: "text",
        backgroundClip: "text",
        color: "transparent",
        filter: "drop-shadow(0 0 26px rgba(255,215,0,0.35))",
        animation: `beerFadeUp 0.8s ease ${delay + 0.12}s both, beerShine 5.5s linear infinite`,
      }}
    >
      BEER MILLIONAIRE
    </Typography>
  </>
);

/** Floating pint with a golden halo behind it. */
export const BeerMug = ({ size = 148, delay = 0, float = true }) => (
  <Box
    sx={{
      position: "relative",
      width: size,
      height: size,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      animation: `beerFadeIn 0.9s ease ${delay}s both`,
    }}
  >
    <Box
      sx={{
        position: "absolute",
        inset: "-18%",
        borderRadius: "50%",
        background:
          "radial-gradient(circle, rgba(255,215,0,0.28) 0%, rgba(255,152,0,0.12) 45%, transparent 70%)",
      }}
    />
    <Box
      component="img"
      src="/beer2.png"
      alt=""
      sx={{
        width: "100%",
        height: "100%",
        objectFit: "contain",
        filter: "drop-shadow(0 10px 26px rgba(255,152,0,0.35))",
        animation: float ? "beerFloat 4.5s ease-in-out infinite" : "none",
      }}
    />
  </Box>
);

/** Trailing "..." that ripples, to signal an open-ended wait. */
export const Dots = () => (
  <Box component="span" sx={{ display: "inline-flex", ml: "1px" }}>
    {[0, 1, 2].map((i) => (
      <Box
        key={i}
        component="span"
        sx={{ animation: `beerDotFade 1.5s ease-in-out ${i * 0.2}s infinite` }}
      >
        .
      </Box>
    ))}
  </Box>
);

/** Small pill used for status lines and metadata. */
export const StatusPill = ({ children, color = BEER.gold, blink = false, delay = 0, sx }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 1,
      px: { xs: 1.75, sm: 2.25 },
      py: { xs: 0.7, sm: 0.85 },
      borderRadius: 999,
      bgcolor: "rgba(13,33,55,0.85)",
      border: `1px solid ${color}55`,
      boxShadow: `0 0 18px ${color}22`,
      animation: `beerFadeUp 0.7s ease ${delay}s both`,
      ...sx,
    }}
  >
    {blink && (
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          bgcolor: color,
          flexShrink: 0,
          animation: "beerBlink 1.6s ease-out infinite",
        }}
      />
    )}
    <Typography
      sx={{
        color,
        fontWeight: 700,
        fontSize: { xs: "0.78rem", sm: "0.9rem" },
        letterSpacing: 0.3,
      }}
    >
      {children}
    </Typography>
  </Box>
);
